import React from "react";
import { Card, CardHeader, CardBody, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { threatCard, threatCardHeader, threatCardBody, threatIconChip } from "./cardStyles";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

// Color scheme for IOC types
const IOC_COLORS = {
  Malicious: '#8b0000',      // Dark Red
  Suspicious: '#dc3545',     // Red (danger)
  Unknown: '#ffc107',        // Yellow (warning)
  Clean: '#198754'           // Green (success)
};

const DetectedIOCs = ({ data, loading }) => {
  if (loading) {
    return (
      <Card 
        className="h-100"
        style={{ 
          ...threatCard
        }}
      >
        <CardHeader style={threatCardHeader}>
          <div className="d-flex align-items-center gap-3">
            <div style={threatIconChip()}>
              <i className="ri-bug-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <h5 className="card-title mb-0">IOC Distribution</h5>
          </div>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "280px", ...threatCardBody }}>
          <Spinner style={{ color: ORANGE_COLORS.primary }} />
        </CardBody>
      </Card>
    );
  }

  const iocData = [
    { name: "Malicious", value: data?.Malicious || 0, color: IOC_COLORS.Malicious, bgClass: 'bg-danger' },
    { name: "Suspicious", value: data?.Suspicious || 0, color: IOC_COLORS.Suspicious, bgClass: 'bg-danger' },
    { name: "Unknown", value: data?.Unknown || 0, color: IOC_COLORS.Unknown, bgClass: 'bg-warning' },
    { name: "Clean", value: data?.Clean || 0, color: IOC_COLORS.Clean, bgClass: 'bg-success' }
  ];

  const totalIOCs = iocData.reduce((sum, ioc) => sum + ioc.value, 0);

  const options = {
    chart: {
      type: "donut",
      toolbar: { show: false },
      fontFamily: "Poppins, sans-serif"
    },
    labels: iocData.map(d => d.name),
    colors: iocData.map(d => d.color),
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Math.round(val)}%`,
      style: {
        fontSize: "11px",
        fontWeight: "600",
        colors: ['#fff']
      },
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        opacity: 0.45
      }
    },
    legend: {
      show: false
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total IOCs',
              fontSize: '14px',
              fontWeight: 600,
              color: ORANGE_COLORS.primary,
              formatter: () => totalIOCs
            },
            value: {
              fontSize: '24px',
              fontWeight: 700,
              color: ORANGE_COLORS.primary,
              formatter: (val) => val
            }
          }
        }
      }
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => `${val} IOC${val !== 1 ? 's' : ''}`
      },
      style: {
        fontSize: '12px'
      }
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.1
        }
      }
    }
  };

  const series = iocData.map(d => d.value);

  return (
    <Card 
      className="h-100"
      style={{ 
        ...threatCard
      }}
    >
      <CardHeader style={threatCardHeader}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div style={threatIconChip()}>
              <i className="ri-bug-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <h5 className="card-title mb-0">IOC Distribution</h5>
          </div>
          {totalIOCs > 0 && (
            <span 
              className="badge" 
              style={{ 
                backgroundColor: 'rgba(255, 107, 53, 0.1)', 
                color: ORANGE_COLORS.primary,
                border: `1px solid ${ORANGE_COLORS.primary}`,
                fontSize: '11px'
              }}
            >
              {totalIOCs} total
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody style={threatCardBody}>
        {totalIOCs > 0 ? (
          <>
            <ReactApexChart options={options} series={series} type="donut" height={240} />
            
            {/* Custom Legend with Bootstrap Classes */}
            <div className="mt-3 pt-3 border-top" style={{ borderColor: 'rgba(255, 107, 53, 0.2)' }}>
              <div className="row g-2">
                {iocData.map((item, index) => (
                  <div key={index} className="col-6">
                    <div 
                      className={`d-flex align-items-center p-2 rounded ${item.bgClass} bg-opacity-10`}
                    >
                      <div 
                        className={`${item.bgClass} rounded`}
                        style={{
                          width: '12px',
                          height: '12px',
                          marginRight: '8px',
                          flexShrink: 0
                        }}
                      ></div>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="text-muted small text-truncate" style={{ fontSize: '11px' }}>
                          {item.name}
                        </div>
                        <div 
                          className={`fw-bold text-${item.bgClass.replace('bg-', '')}`}
                          style={{ fontSize: '14px' }}
                        >
                          {item.value}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <div className="mx-auto mb-3" style={threatIconChip()}>
              <i className="ri-bug-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <p className="text-muted mb-0">No IOCs detected</p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default DetectedIOCs;
